import { gql } from "@apollo/client";
import { useMemo } from "react";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { InvestigationCatalogueItem } from "../collections/investigation-catalogue-item";
import type { RxDBCollections } from "../provider";
import {
  InvestigationCatalogueItemPullDocument,
  type WholeSyncableInvestigationCatalogueItemFragment,
} from "./__generated__/investigation-catalogue-item.gql";
import { useGqlReplication } from "./use-gql-replication";

const BIGGER_THAN_ACTUAL_ITEMS_SIZE = 1000;

export const useReplicateInvestigationCatalogueItem = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<InvestigationCatalogueItem>;
}) => {
  const pull = useMemo(
    () => ({
      query: InvestigationCatalogueItemPullDocument,
      buildInputVariables: async (checkpoint, batchSize) => ({
        input: {
          limit: batchSize,
          checkpoint,
        },
      }),
      buildPullCheckpoint: async (doc) => ({
        id: doc.id,
        updatedAt: doc.meta.updatedAt,
      }),
      reSyncInterval: { hours: 1 },
    }),
    [],
  );

  return useGqlReplication<
    InvestigationCatalogueItem,
    WholeSyncableInvestigationCatalogueItemFragment,
    null
  >({
    db,
    collection,
    pull,
    mapGqlToRx: mapGqlInvestigationCatalogueItemToRxInvestigationCatalogueItem,
    batchSize: BIGGER_THAN_ACTUAL_ITEMS_SIZE,
    metadataLocalStorageKeySuffix: "investigation-catalogue-item",
  });
};

export const mapGqlInvestigationCatalogueItemToRxInvestigationCatalogueItem = (
  catalogueItem: WholeSyncableInvestigationCatalogueItemFragment,
): WithDeleted<InvestigationCatalogueItem> => ({
  id: catalogueItem.id,
  // TODO: meta should be made non-nullable on the backend
  meta: {
    updatedAt: catalogueItem.meta?.updatedAt ?? new Date().toISOString(),
    createdAt: catalogueItem.meta?.createdAt ?? new Date().toISOString(),
    updatedByUserId: catalogueItem.meta?.updatedByUserId ?? "",
    createdByUserId: catalogueItem.meta?.createdByUserId ?? "",
  },
  title: { en: catalogueItem.title.en },
  result: catalogueItem.result ?? null,
  activityType: catalogueItem.activityType,
  coding: {
    elephant: catalogueItem.coding?.elephant
      ? {
          code: catalogueItem.coding.elephant.code,
          system: catalogueItem.coding.elephant.system ?? null,
        }
      : null,
    loinc: catalogueItem.coding?.loinc
      ? {
          code: catalogueItem.coding.loinc.code,
          system: catalogueItem.coding.loinc.system ?? null,
        }
      : null,
  },
  version: catalogueItem.version,
  keywords: catalogueItem.keywords
    ? catalogueItem.keywords.filter(
        (keyword): keyword is { en: string } => keyword !== null,
      )
    : null,
  _deleted: catalogueItem.meta?.deleted ?? false,
});

gql`
  fragment WholeSyncableInvestigationCatalogueItem on SyncableInvestigationCatalogueItem {
      activityType
      coding {
        elephant {
          code
          display
          system
        }
        loinc {
          code
          display
          system
        }
      }
      version
      id
      keywords {
        en
      }
      result {
        resultSchema
      }
      title {
        en
      }
      meta {
        deleted
        updatedAt
        createdAt
        updatedByUserId
        createdByUserId
      }
    }
   `;

gql`
  query InvestigationCatalogueItemPull($input:PullInvestigationCatalogueItemInput!) {
    pullInvestigationCatalogueItem(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncableInvestigationCatalogueItem
      }
    }
  }
`;
